<script setup lang="ts">
/**
 * SG Incorporation (Pre) — E-Filing review wizard (task code B32109).
 *
 * A multi-step confirm-before-submit screen mirroring test_incorporation.html.
 * Auto-filled from the setup-entity incorporation data + uploaded files; the
 * reviewed model is serialized to the `/bizfile/incorporation/create` payload
 * and emitted. The actual Singpass-authenticated bizfile submission is handled
 * by the host flow — this component never enters credentials.
 *
 * Presented as a "preset document package" (stage stepper + paper cards) — same
 * look as the Incorporation (Pre) document screen, not the generic e-filing shell.
 */
import { computed, nextTick, onMounted, ref } from 'vue';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useDialog } from '@/shared/composables/useDialog';
// 인코퍼레이션은 환경 무관하게 항상 파이썬 QR 경로 노출.
// 실제 활성 여부는 백엔드 pyEnabled() 응답(SG_NEW_DAEMON_URL/mTLS 세팅 유무)이 결정한다.
import SgPyLoginDialog from '../change-financial-year-end/SgPyLoginDialog.vue';
import {
  sgPyScrapingApi,
  type SgPyReservedName,
} from '../change-financial-year-end/composables/sgPyScrapingApi';
import { useIncorporationEfiling } from './composables/useIncorporationEfiling';
import EntityInfoStep from './steps/EntityInfoStep.vue';
import PositionHoldersStep from './steps/PositionHoldersStep.vue';
import NomineesStep from './steps/NomineesStep.vue';
import ControllersStep from './steps/ControllersStep.vue';
import SharesStep from './steps/SharesStep.vue';
import ConstitutionStep from './steps/ConstitutionStep.vue';
import ReviewStep from './steps/ReviewStep.vue';

interface Props {
  taskData?: any;
  taskId?: string | number;
  entityCd?: string;
  taskConfig?: any;
  embedded?: boolean;
  showBackButton?: boolean;
  readOnly?: boolean;
  /**
   * 커스텀폼 기반 My Preset(Singbac Incorporation Pre 등) — icpCd 대신 이 SgIncorporationForm
   * 으로 모델을 자동 시드한다. 없으면 기존 icpCd 경로.
   */
  prefillForm?: any;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: '',
  entityCd: '',
  taskConfig: null,
  embedded: true,
  showBackButton: true,
  readOnly: false,
  prefillForm: null,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  complete: [data: any];
}>();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
const tr = (key: string, fallback: string) => t(`startTask.sgIncorporationEfiling.${key}`) || fallback;

const { state, load, saveEfilingModel, buildPayload } = useIncorporationEfiling();

const icpCd = computed(() =>
  String(
    props.taskData?.icpCd ||
      props.taskData?.formData?.icpCd ||
      props.taskData?.formData?.icp_cd ||
      props.entityCd ||
      props.taskData?.entityCd ||
      '',
  ).trim(),
);

/**
 * 주식수량 합계 검증 — 서브클래스 allotted 합계는 상위(주식종류/배정선/그룹라인)의
 * shareAllotted 를 초과할 수 없다. 초과 시 제출 차단 메시지 반환('' = 정상).
 */
const shareValidationError = computed(() => {
  const m = state.model;
  const sumSub = (subs?: { shareAllotted: string }[]) =>
    (subs || []).reduce((acc, s) => acc + (Number(s.shareAllotted) || 0), 0);
  const over = (main: string, subs?: { shareAllotted: string }[]) =>
    (subs || []).length > 0 && sumSub(subs) > (Number(main) || 0);
  for (const c of m.currencies || []) {
    for (const k of c.shareClasses || []) {
      if (over(k.shareAllotted, k.subClasses)) {
        return tr('subOverflowClass', 'A share class sub-class total exceeds its number of shares.');
      }
    }
  }
  for (const l of m.allotLines || []) {
    if (over(l.shareAllotted, l.subClasses)) {
      return tr('subOverflowAllot', 'An allotment sub-class total exceeds its number of shares.');
    }
  }
  for (const g of m.groups || []) {
    for (const l of g.lines || []) {
      if (over(l.shareAllotted, l.subClasses)) {
        return tr('subOverflowGroup', 'A group sub-class total exceeds its number of shares.');
      }
    }
  }
  // 자본 총량 == 배정 총량 — 개별 배정선(B) + 그룹 배정(C) 을 모두 shareholder 배정으로 합산.
  // (통화|주식종류 별) 자본 shareAllotted 합계와 (배정선 + 그룹라인) shareAllotted 합계가 일치해야 함.
  const cap: Record<string, number> = {};
  for (const c of m.currencies || []) {
    for (const k of c.shareClasses || []) {
      const key = `${c.currency}|${k.shareType}`;
      cap[key] = (cap[key] || 0) + (Number(k.shareAllotted) || 0);
    }
  }
  const alloc: Record<string, number> = {};
  for (const l of m.allotLines || []) {
    const key = `${l.currency}|${l.shareType}`;
    alloc[key] = (alloc[key] || 0) + (Number(l.shareAllotted) || 0);
  }
  for (const g of m.groups || []) {
    for (const l of g.lines || []) {
      const key = `${l.currency}|${l.shareType}`;
      alloc[key] = (alloc[key] || 0) + (Number(l.shareAllotted) || 0);
    }
  }
  for (const key of new Set([...Object.keys(cap), ...Object.keys(alloc)])) {
    if ((cap[key] || 0) !== (alloc[key] || 0)) {
      return tr(
        'shareBalance',
        'Total allotted shares (including group allotments) must equal the share capital.',
      );
    }
  }
  return '';
});

// ── Area code 밸리데이션 (SG 아님 + area code 비었고 "no area code" 미체크 → 오류) ──
const b05DialByCode = ref<Record<string, string>>({});
const b05Loaded = ref(false);
let b05Promise: Promise<void> | null = null;
async function loadB05Dial(): Promise<void> {
  if (b05Loaded.value) return;
  if (b05Promise) return b05Promise;
  b05Promise = (async () => {
    try {
      const res = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
      const codes = (res as any).codes || (res as any).data?.codes || [];
      const map: Record<string, string> = {};
      for (const c of codes) {
        const total = String(c.totalCode || c.TOTAL_CODE || '').trim();
        const b1 = String(c.bigo1 || c.BIGO1 || '').trim();
        const b3 = String(c.bigo3 || c.BIGO3 || '').trim();
        const dial = (/^\+?\d+$/.test(b1) ? b1 : /^\+?\d+$/.test(b3) ? b3 : b1 || b3).replace(/^\+/, '');
        if (total) map[total] = dial;
      }
      b05DialByCode.value = map;
      b05Loaded.value = true;
    } catch {
      b05DialByCode.value = {};
    }
  })();
  return b05Promise;
}
function isSgCode(code: string): boolean {
  const cc = String(code || '').trim();
  if (!cc) return false;
  if (cc.replace(/^\+/, '') === '65') return true;
  if (/singapore/i.test(cc)) return true;
  return b05DialByCode.value[cc] === '65';
}
/** 국가 선택됨 + SG 아님 + NA 미체크 + area code 비었으면 true */
function needsAreaCode(country: string, area: string, na: boolean): boolean {
  if (na) return false;
  const cc = String(country || '').trim();
  if (!cc) return false;
  // B05 dial 맵이 아직 로드 안 됐고 dial/이름으로도 SG 판별이 안 되면, 잘못 걸리지 않도록 검증 스킵.
  if (!b05Loaded.value && !(cc.replace(/^\+/, '') === '65') && !/singapore/i.test(cc)) return false;
  if (isSgCode(cc)) return false;
  return !String(area || '').trim();
}

/** 개인 사람 기본정보(주소·전화·이메일) 중 하나라도 누락됐는지 */
function personContactMissing(h: {
  contactAddress?: {
    postalCode?: string;
    foreignAddressLine1?: string;
    streetName?: string;
    blockNo?: string;
    buildingName?: string;
  };
  mobileNo?: string;
  email?: string;
}): boolean {
  const a = h.contactAddress;
  const hasAddr =
    !!a &&
    (!!String(a.postalCode || '').trim() ||
      !!String(a.foreignAddressLine1 || '').trim() ||
      !!String(a.streetName || '').trim() ||
      !!String(a.blockNo || '').trim() ||
      !!String(a.buildingName || '').trim());
  const hasPhone = !!String(h.mobileNo || '').trim();
  const hasEmail = !!String(h.email || '').trim();
  return !hasAddr || !hasPhone || !hasEmail;
}

interface EfIssue {
  stepKey: string;
  message: string;
  holderIndex?: number;
  controllerIndex?: number;
  nominatorIndex?: number;
}

/** 회사정보(Entity) 스텝 검증 — 등록사무소 주소 필수 + Level/Unit 최대 5자 + 이메일 필수. */
const entityInfoIssue = computed<EfIssue | null>(() => {
  const e = state.model.entityInfo;
  if (!e) return null;
  const blank = (v: unknown) => !String(v ?? '').trim();
  const a = e.registeredAddress;
  if (a && a.addressTypeCd === 'B07002') {
    if (blank(a.foreignAddressLine1)) {
      return { stepKey: 'entity', message: tr('entityAddrForeign', 'Enter the registered office foreign address (line 1).') };
    }
  } else if (a) {
    // 우편번호만 필수(Block/Street/Building 은 필수 아님).
    if (blank(a.postalCode)) {
      return {
        stepKey: 'entity',
        message: tr('entityAddrRequired', 'Enter the registered office postal code.'),
      };
    }
    // "층/호수 없음" 미체크(haveLevelUnit !== false) → Level/Unit 은 필수 + 최대 5자.
    if (a.haveLevelUnit !== false) {
      if (blank(a.level) || blank(a.unit)) {
        return {
          stepKey: 'entity',
          message: tr('entityLevelUnitRequired', 'Enter Level and Unit, or tick "Address doesn\'t have level and unit".'),
        };
      }
      if (String(a.level ?? '').trim().length > 5 || String(a.unit ?? '').trim().length > 5) {
        return {
          stepKey: 'entity',
          message: tr('entityLevelUnitMax', 'Level and Unit must be 5 characters or fewer.'),
        };
      }
    }
  }
  if (blank(e.email)) {
    return { stepKey: 'entity', message: tr('entityEmailRequired', 'Enter the entity email.') };
  }
  return null;
});

/** 첫 밸리데이션 문제(제출 차단 대상). 없으면 null. 모든 스텝을 검사한다. */
const firstIssue = computed<EfIssue | null>(() => {
  const m = state.model;
  if (entityInfoIssue.value) return entityInfoIssue.value;
  if (shareValidationError.value) {
    return { stepKey: 'shares', message: shareValidationError.value };
  }
  const areaMsg = tr(
    'areaCodeIssue',
    'A contact is missing its area code — enter it, or tick "no area code".',
  );
  const contactMsg = tr(
    'contactInfoIssue',
    'A person is missing address, mobile, or email — all three are required.',
  );
  const idTypeMsg = tr('idTypeRequired', 'Select an ID type for each individual position holder.');
  const holders = m.positionHolders || [];
  for (let i = 0; i < holders.length; i += 1) {
    const h = holders[i];
    // 개인(category '1') 은 ID Type 필수.
    if (h.category === '1' && !String(h.idType || '').trim()) {
      return { stepKey: 'holders', holderIndex: i, message: idTypeMsg };
    }
    if (h.category === '1' && personContactMissing(h)) {
      return { stepKey: 'holders', holderIndex: i, message: contactMsg };
    }
    if (needsAreaCode(h.mobileCountryCode, h.mobileAreaCode, h.areaCodeNotApplicable)) {
      return { stepKey: 'holders', holderIndex: i, message: areaMsg };
    }
  }
  const nominators = m.nominators || [];
  for (let i = 0; i < nominators.length; i += 1) {
    const n = nominators[i];
    if (needsAreaCode(n.countryCode, n.areaCode, n.noArea)) {
      return { stepKey: 'nominees', nominatorIndex: i, message: areaMsg };
    }
  }
  const controllers = m.controllers || [];
  for (let i = 0; i < controllers.length; i += 1) {
    const c = controllers[i];
    if (needsAreaCode(c.countryCode, c.areaCode, c.noArea)) {
      return { stepKey: 'controllers', controllerIndex: i, message: areaMsg };
    }
  }
  return null;
});

// 밸리데이션 포커스 시 해당 스텝 컴포넌트의 아코디언을 여는 인덱스
const holdersOpenIndex = ref<number | null>(null);
const controllersOpenIndex = ref<number | null>(null);
const nomineesOpenIndex = ref<number | null>(null);
// 기본정보(주소·전화·이메일) 누락 빨간 하이라이트 대상 holder 인덱스
const holdersContactErrorIndex = ref<number | null>(null);
// 스텝 영역 v-form — 제출 시 validate() 로 필드 rule(빨간색) 트리거
const efForm = ref<{ validate?: () => Promise<unknown> } | null>(null);

const steps = computed(() => [
  { key: 'entity', label: tr('stepEntity', 'Entity information'), short: 'Entity' },
  { key: 'holders', label: tr('stepHolders', 'Position holders'), short: 'Holders' },
  { key: 'nominees', label: tr('stepNominees', 'Nominees'), short: 'Nominees' },
  { key: 'controllers', label: tr('stepControllers', 'Controllers'), short: 'Controllers' },
  { key: 'shares', label: tr('stepShares', 'Share capital'), short: 'Shares' },
  { key: 'constitution', label: tr('stepConstitution', 'Constitution & documents'), short: 'Documents' },
  { key: 'review', label: tr('stepReview', 'Review & submit'), short: 'Review' },
]);

const current = ref(0);
const isLast = computed(() => current.value === steps.value.length - 1);
const isFirst = computed(() => current.value === 0);
const submitting = ref(false);

// 문서 확대(Pre 문서화면과 동일)
const ZOOM_MIN = 100;
const ZOOM_MAX = 175;
const ZOOM_STEP = 25;
const docZoom = ref(100);
const canZoomIn = computed(() => docZoom.value < ZOOM_MAX);
const canZoomOut = computed(() => docZoom.value > ZOOM_MIN);
function zoomIn() {
  docZoom.value = Math.min(ZOOM_MAX, docZoom.value + ZOOM_STEP);
}
function zoomOut() {
  docZoom.value = Math.max(ZOOM_MIN, docZoom.value - ZOOM_STEP);
}
function zoomReset() {
  docZoom.value = 100;
}
const confirmed = ref(false);
const assembledPayload = ref<Record<string, unknown> | null>(null);

/* ── DEV-LOCAL: 로컬 Python 로그인(QR/비밀번호) → 예약회사 선택 → create ── */
const sessionStore = useSessionStore();
const { warning: showWarning } = useDialog();
const isDevLocal = true;
const pyEnabled = ref(false);
const showPyLogin = ref(false);
const pyBusy = ref(false);
const pyFilingAgentUEN = ref('');
const pyCtx = ref<{ sessionId: string; mstIdx: number | null }>({ sessionId: '', mstIdx: null });
const showReservedDialog = ref(false);
const reservedList = ref<SgPyReservedName[]>([]);
const selectedUniqueId = ref('');
const reservedOptions = computed(() =>
  reservedList.value.map((r) => ({
    title:
      `${r.proposed_entity_name} ${r.suffix_name || r.proposed_suffix}`.trim() +
      ` (${r.transaction_id})`,
    value: r.transaction_id,
  })),
);

function goStep(i: number) {
  if (i >= 0 && i < steps.value.length) current.value = i;
}
function next() {
  if (!isLast.value) current.value += 1;
}
function prev() {
  if (!isFirst.value) current.value -= 1;
}

/**
 * Assemble the reviewed model into the bizfile create-payload and hand it off.
 * The actual Singpass-authenticated ACRA submission is performed by the host
 * flow — this screen only confirms + prepares the data, so it does NOT mark the
 * task as filed.
 */
async function handleSubmit() {
  if (submitting.value || pyBusy.value) return;
  // 버튼을 누르면 (티링크/bizfile 제출과 무관하게) 현재 편집본을 먼저 TASK_DATA 에 저장.
  // 밸리데이션 실패로 제출을 못 하더라도 편집 내용은 영속화되어 재진입 시 복원된다.
  if (!props.readOnly) {
    await saveEfilingModel(props.taskId);
  }
  // area code SG 판별용 B05 dial 맵이 로드된 뒤 검증(로드 전이면 SG 오탐 방지).
  await loadB05Dial();
  const issue = firstIssue.value;
  if (issue) {
    // 문제 있는 스텝으로 이동 + (사람지정) 해당 아코디언 열기 + 포커스
    const idx = steps.value.findIndex((s) => s.key === issue.stepKey);
    if (idx >= 0) current.value = idx;
    // watch 가 같은 값 재지정에도 반응하도록 null → 값 순으로 세팅
    holdersOpenIndex.value = null;
    controllersOpenIndex.value = null;
    nomineesOpenIndex.value = null;
    holdersContactErrorIndex.value = null;
    await nextTick();
    if (typeof issue.holderIndex === 'number') holdersOpenIndex.value = issue.holderIndex;
    // 기본정보 누락 이슈면 해당 holder 의 빈 필드에 빨간 테두리
    if (
      issue.stepKey === 'holders' &&
      typeof issue.holderIndex === 'number' &&
      personContactMissing((state.model.positionHolders || [])[issue.holderIndex] || {})
    ) {
      holdersContactErrorIndex.value = issue.holderIndex;
    }
    if (typeof issue.controllerIndex === 'number') controllersOpenIndex.value = issue.controllerIndex;
    if (typeof issue.nominatorIndex === 'number') nomineesOpenIndex.value = issue.nominatorIndex;
    await nextTick();
    // 필드 rule 트리거(빨간색) → 열린 아코디언의 area code 등이 빨갛게 표시됨
    try {
      await efForm.value?.validate?.();
    } catch { /* ignore */ }
    await nextTick();
    // 현재 스텝의 첫 에러 필드로 스크롤·포커스
    const errEl = Array.from(
      document.querySelectorAll('.paper-zoom .v-input--error'),
    ).find((el) => (el as HTMLElement).offsetParent !== null) as HTMLElement | undefined;
    if (errEl) {
      errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (errEl.querySelector('input, textarea, [tabindex]') as HTMLElement | null)?.focus?.();
    }
    await showWarning(tr('validationTitle', 'Check the highlighted fields'), issue.message);
    return;
  }
  assembledPayload.value = buildPayload();
  // DEV-LOCAL: 실제 bizfile 제출(로그인 → 예약회사 선택 → create)
  if (isDevLocal && pyEnabled.value && !props.readOnly) {
    pyFilingAgentUEN.value = String(sessionStore.getCustomerInfo()?.regNo || '')
      .replace(/\s/g, '')
      .trim();
    showPyLogin.value = true;
    return;
  }
  // 운영/비-로컬: 조립 + 확인만 (실제 제출은 host 흐름)
  confirmed.value = true;
}

async function onPyAuthenticated(payload: {
  sessionId: string;
  mstIdx: number | null;
  filingAgentUEN: string;
}) {
  showPyLogin.value = false;
  pyCtx.value = { sessionId: payload.sessionId, mstIdx: payload.mstIdx };
  pyBusy.value = true;
  let list: SgPyReservedName[] = [];
  try {
    const r = await sgPyScrapingApi.reservedNames(payload.sessionId, payload.mstIdx);
    list = r.reservedNames || [];
    if (!list.length) {
      pyBusy.value = false;
      await showWarning(
        tr('errorTitle', 'E-Filing Error'),
        r.error || tr('noReserved', 'No reserved companies found for this account.'),
      );
      return;
    }
  } catch (e: any) {
    pyBusy.value = false;
    await showWarning(
      tr('errorTitle', 'E-Filing Error'),
      e?.message || tr('loadReservedFailed', 'Failed to load reserved companies.'),
    );
    return;
  }
  // 항상 예약회사 목록을 보여주고 사용자가 확인·선택해야 진행(자동 진행 금지).
  // 이 인코퍼레이션 회사명과 일치하는 항목을 기본 선택.
  pyBusy.value = false;
  reservedList.value = list;
  const target = String(state.model.entityInfo.companyName || '').replace(/\s+/g, '').toUpperCase();
  const match = target
    ? list.find(
        (r) => `${r.proposed_entity_name || ''}`.replace(/\s+/g, '').toUpperCase() === target,
      )
    : undefined;
  selectedUniqueId.value = (match || list[0]).transaction_id;
  showReservedDialog.value = true;
}

function onSelectReserved() {
  if (!selectedUniqueId.value) return;
  void proceedCreate(selectedUniqueId.value);
}

async function proceedCreate(uniqueId: string) {
  showReservedDialog.value = false;
  pyBusy.value = true;
  try {
    const r = await sgPyScrapingApi.createIncorporation({
      taskId: props.taskId,
      sessionId: pyCtx.value.sessionId,
      uniqueId,
      mstIdx: pyCtx.value.mstIdx,
      payload: assembledPayload.value || buildPayload(),
    });
    if (r.success) {
      // 라이브 제출 성공과 동일하게 처리(부모 handleEFilingComplete → 태스크 완료 반영)
      emit('complete', { icpCd: state.icpCd, draftId: r.draftId });
    } else {
      await showWarning(
        tr('errorTitle', 'E-Filing Error'),
        r.error || tr('filingFailed', 'Incorporation filing failed. Please try again.'),
      );
    }
  } catch (e: any) {
    await showWarning(
      tr('errorTitle', 'E-Filing Error'),
      e?.message || tr('filingFailed', 'Incorporation filing failed. Please try again.'),
    );
  } finally {
    pyBusy.value = false;
  }
}

function getBizfilePayload() {
  return {
    icpCd: state.icpCd,
    taskId: props.taskId ? Number(props.taskId) : null,
    companyName: state.model.entityInfo.companyName,
    bizfilePayload: assembledPayload.value || buildPayload(),
  };
}

defineExpose({ getBizfilePayload, model: () => state.model });

onMounted(async () => {
  void loadB05Dial();
  void load({
    icpCd: icpCd.value,
    taskId: props.taskId,
    prefillForm: props.prefillForm || null,
    companyName: String(props.taskData?.companyName || props.taskData?.entityName || '').trim(),
  });
  if (isDevLocal) {
    pyEnabled.value = await sgPyScrapingApi.enabled();
  }
});
</script>

<template>
  <div class="sg-inc-efpkg">
    <!-- bizfile 제출(예약회사 조회 / create) 진행 중 인디케이터 -->
    <v-overlay
      :model-value="pyBusy"
      class="align-center justify-center"
      persistent
      scrim="rgba(15,23,42,0.55)"
    >
      <div class="ef-submit-loading">
        <v-progress-circular indeterminate size="56" width="5" color="primary" />
        <div class="ef-submit-loading__txt">
          {{ tr('submittingBizfile', 'Submitting to ACRA bizfile…') }}
        </div>
      </div>
    </v-overlay>

    <div class="sg-inc-efpkg__head">
      <div class="sg-inc-efpkg__title">
        {{ tr('title', 'Incorporation E-Filing') }}
        <span v-if="state.companyName" class="sg-inc-efpkg__company">— {{ state.companyName }}</span>
      </div>
      <div class="sg-inc-efpkg__sub">
        {{ tr('subtitle', 'Review the pre-filled details before submitting to ACRA bizfile.') }}
      </div>
    </div>

    <!-- Stage stepper -->
    <div class="stepper">
      <button
        v-for="(s, i) in steps"
        :key="s.key"
        type="button"
        class="step"
        :class="{ active: current === i, done: current > i }"
        :title="`Stage ${i + 1} · ${s.label}`"
        @click="goStep(i)"
      >
        <span class="step-no">{{ current > i ? '✓' : i + 1 }}</span>
        <span class="step-txt">
          <b>Stage {{ i + 1 }}</b>
          <small>{{ s.short }}</small>
        </span>
      </button>
    </div>

    <!-- Document zoom -->
    <div class="zoom-bar">
      <div class="doc-zoom" role="group" aria-label="Larger text">
        <button type="button" class="doc-zoom-btn" :disabled="!canZoomOut" title="Smaller" @click="zoomOut">
          A&minus;
        </button>
        <button type="button" class="doc-zoom-pct" title="Reset size" @click="zoomReset">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="!canZoomIn" title="Larger" @click="zoomIn">
          A+
        </button>
      </div>
    </div>

    <!-- Loading / error -->
    <div v-if="state.loading" class="sg-inc-efpkg__state">
      <v-progress-circular indeterminate color="primary" size="30" />
      <span>{{ tr('loading', 'Loading incorporation data…') }}</span>
    </div>
    <div v-else-if="state.error" class="sg-inc-efpkg__state">
      <v-alert type="error" variant="tonal" density="compact">{{ state.error }}</v-alert>
    </div>

    <!-- Stages (paper) -->
    <v-form v-else ref="efForm" class="paper-zoom" :style="{ zoom: docZoom / 100 }" @submit.prevent>
      <section v-for="(s, i) in steps" v-show="current === i" :key="s.key" class="stage">
        <div class="stage-head">
          <span class="stage-badge">Stage {{ i + 1 }}</span>
          <span class="stage-t">{{ s.label }}</span>
        </div>

        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="nm">{{ s.label }}</span>
            <span class="no">Step {{ i + 1 }} / {{ steps.length }}</span>
          </div>

          <div class="simple-frame">
            <EntityInfoStep
              v-if="s.key === 'entity'"
              :entity-info="state.model.entityInfo"
              :readonly="readOnly"
            />
            <PositionHoldersStep
              v-else-if="s.key === 'holders'"
              :holders="state.model.positionHolders"
              :open-index="holdersOpenIndex"
              :contact-error-index="holdersContactErrorIndex"
              :readonly="readOnly"
            />
            <NomineesStep
              v-else-if="s.key === 'nominees'"
              :nominators="state.model.nominators"
              :nominee-exempt="state.model.nomineeExempt"
              :holders="state.model.positionHolders"
              :open-index="nomineesOpenIndex"
              :readonly="readOnly"
            />
            <ControllersStep
              v-else-if="s.key === 'controllers'"
              :controllers="state.model.controllers"
              :rorc="state.model.rorc"
              :uploaded-files="state.uploadedFiles"
              :open-index="controllersOpenIndex"
              :readonly="readOnly"
            />
            <SharesStep
              v-else-if="s.key === 'shares'"
              :currencies="state.model.currencies"
              :allot-lines="state.model.allotLines"
              :groups="state.model.groups"
              :holders="state.model.positionHolders"
              :uploaded-files="state.uploadedFiles"
              :readonly="readOnly"
            />
            <ConstitutionStep
              v-else-if="s.key === 'constitution'"
              :constitution="state.model.constitution"
              :uploaded-files="state.uploadedFiles"
              :generated-docs="state.generatedDocs"
              :readonly="readOnly"
            />
            <ReviewStep v-else-if="s.key === 'review'" :model="state.model" />
          </div>

          <v-alert
            v-if="s.key === 'review' && confirmed"
            type="success"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ tr('confirmed', 'Details confirmed and prepared. Proceed with the ACRA submission (Singpass) to complete the filing.') }}
          </v-alert>
        </div>
      </section>
    </v-form>

    <!-- Stage nav -->
    <div class="stage-nav">
      <button
        v-if="isFirst && showBackButton"
        type="button"
        class="nav-btn"
        @click="emit('back')"
      >
        &larr; {{ tr('back', 'Back') }}
      </button>
      <button v-else-if="!isFirst" type="button" class="nav-btn" @click="prev">
        &larr; {{ tr('previous', 'Previous') }}
      </button>

      <span class="nav-count">Stage {{ current + 1 }} / {{ steps.length }}</span>

      <button
        v-if="!isLast"
        type="button"
        class="nav-btn primary"
        :disabled="state.loading || !!state.error"
        @click="next"
      >
        {{ tr('next', 'Next') }} &rarr;
      </button>
      <button
        v-else-if="!readOnly"
        type="button"
        class="nav-btn primary"
        :disabled="state.loading || !!state.error || submitting || pyBusy"
        @click="handleSubmit"
      >
        <span v-if="submitting || pyBusy">…</span>
        <span v-else>{{ confirmed ? tr('confirmedBtn', 'Confirmed') : tr('submit', 'Submit for E-Filing') }}</span>
      </button>
    </div>

    <SgPyLoginDialog
      v-model="showPyLogin"
      :task-id="String(taskId)"
      :filing-agent-u-e-n="pyFilingAgentUEN"
      @authenticated="onPyAuthenticated"
    />

    <v-dialog v-model="showReservedDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Select reserved company</v-card-title>
        <v-divider />
        <v-card-text>
          <v-select
            v-model="selectedUniqueId"
            :items="reservedOptions"
            label="Reserved company"
            density="compact"
            variant="outlined"
            hide-details
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end">
          <v-btn variant="text" class="text-none" @click="showReservedDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" class="text-none" :loading="pyBusy" @click="onSelectReserved">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.sg-inc-efpkg {
  color: #111;
  padding: 12px 0 20px;
}
.sg-inc-efpkg__head {
  width: 794px;
  max-width: 100%;
  margin: 0 auto 12px;
  padding: 0 4px;
  box-sizing: border-box;
}
.sg-inc-efpkg__title {
  font-size: 16px;
  font-weight: 800;
  color: #1f2d4d;
}
.sg-inc-efpkg__company {
  font-weight: 600;
  color: #42506a;
}
.sg-inc-efpkg__sub {
  font-size: 12.5px;
  color: #7a869c;
  margin-top: 2px;
}
.sg-inc-efpkg__state {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  width: 794px;
  max-width: 100%;
  margin: 24px auto;
  color: #6b7a94;
  font-size: 13px;
}

/* stepper */
.stepper {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  width: 794px;
  max-width: 100%;
  margin: 0 auto 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(20, 30, 60, 0.06);
  box-sizing: border-box;
  overflow-x: auto;
}
.step {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: #f6f8fc;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
}
.step:hover {
  background: #eef3fb;
}
.step.active {
  background: #eaf1ff;
  border-color: #b9d0ff;
}
.step.done {
  background: #eafaf1;
}
.step-no {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #c9d3e4;
  color: #fff;
  font-weight: 800;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.step.active .step-no {
  background: #1b64da;
}
.step.done .step-no {
  background: #22a06b;
}
.step-txt {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.25;
  min-width: 0;
}
.step-txt b {
  font-size: 11.5px;
  color: #1f2d4d;
  white-space: nowrap;
}
.step-txt small {
  font-size: 10px;
  color: #7a869c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* zoom */
.zoom-bar {
  display: flex;
  justify-content: flex-end;
  width: 794px;
  max-width: 100%;
  margin: 0 auto 18px;
  box-sizing: border-box;
}
.doc-zoom {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f2f4f6;
  border-radius: 10px;
  padding: 2px;
}
.doc-zoom-btn,
.doc-zoom-pct {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 800;
  color: #4b5563;
  border-radius: 8px;
  line-height: 1;
}
.doc-zoom-btn {
  min-width: 36px;
  height: 32px;
  font-size: 15px;
}
.doc-zoom-btn:hover:not(:disabled) {
  background: #fff;
  color: #191f28;
}
.doc-zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.doc-zoom-pct {
  min-width: 52px;
  height: 32px;
  font-size: 12px;
  color: #3182f6;
}

/* paper / stages */
.paper-zoom {
  width: 794px;
  max-width: 100%;
  margin: 0 auto;
}
.stage {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
}
.stage-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 18px;
  padding: 4px 2px 14px;
  border-bottom: 1px solid #eef1f6;
}
.stage-badge {
  flex: none;
  background: #1b64da;
  color: #fff;
  font-weight: 800;
  font-size: 12.5px;
  padding: 5px 12px;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.stage-t {
  font-weight: 800;
  font-size: 15px;
  color: #1f2d4d;
}
.pkg-doc {
  width: 100%;
  margin: 0 auto;
}
.pkg-doclabel {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 12px;
}
.pkg-doclabel .nm {
  font-size: 12.5px;
  font-weight: 800;
  color: #42506a;
  letter-spacing: 0.01em;
}
.pkg-doclabel .no {
  margin-left: auto;
  font-size: 10.5px;
  font-weight: 800;
  color: #9aa6bd;
  white-space: nowrap;
}
.simple-frame {
  width: 100%;
  background: #fff;
  border: 1px solid #e2e5ea;
  box-shadow: 0 4px 20px rgba(20, 30, 60, 0.09);
  padding: 28px;
  box-sizing: border-box;
  overflow: visible;
}

/* nav */
.stage-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 794px;
  max-width: 100%;
  margin: 36px auto 12px;
  padding-top: 8px;
}
.nav-btn {
  border: 1px solid #d3dceb;
  background: #fff;
  color: #42506a;
  font-size: 13px;
  font-weight: 800;
  border-radius: 10px;
  padding: 11px 20px;
  cursor: pointer;
}
.nav-btn:hover:not(:disabled) {
  background: #f2f6fc;
}
.nav-btn.primary {
  background: #1b64da;
  border-color: #1b64da;
  color: #fff;
}
.nav-btn.primary:hover:not(:disabled) {
  background: #1550b8;
}
.nav-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.nav-count {
  font-size: 12.5px;
  font-weight: 800;
  color: #8a97b5;
}
.ef-submit-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  background: #fff;
  padding: 28px 36px;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}
.ef-submit-loading__txt {
  font-size: 14px;
  font-weight: 600;
  color: #1a2336;
}
</style>
